// components/Header.js
'use client'

import { useState, useEffect } from 'react'
import { supabase } from '../_utils/supabase'
import { useRouter, usePathname } from 'next/navigation'
import HeaderDropdown from './HeaderDropdown'
import Logo from './Logo'
import MainNav from './_ui/MainNav'
import { Space_Grotesk } from 'next/font/google';

// ADDED: Instantiate font object outside component
const spaceGrotesk = Space_Grotesk({
  subsets: ['latin'],
  weight: ['700'], // Ensures the font is bold
});

// Create a custom event for scroll updates
export const emitScrollUpdate = (scrollTop) => {
  const event = new CustomEvent('profileScroll', { detail: { scrollTop } })
  document.dispatchEvent(event)
}

// Add header visibility event emitter
// Updated to emit an object with state and scrollTop
export const emitHeaderVisibility = (detail) => {
  const event = new CustomEvent('headerVisibility', { detail })
  document.dispatchEvent(event)
}

export default function Header() {
  const [user, setUser] = useState(null)
  const [headerState, setHeaderState] = useState('full') // 'full', 'icon', or 'hidden'
  const [lastScrollY, setLastScrollY] = useState(0)
  const [scrollTimer, setScrollTimer] = useState(null)
  const [currentProfile, setCurrentProfile] = useState(null)
  const router = useRouter()
  const pathname = usePathname()
  const pathUsername = pathname?.match(/^\/([^\/]+)/)?.[1] || ''

  // Check for homepage, sign-up, and login pages
  const isHomePage = pathname === '/'
  const isSignUpPage = pathname === '/sign-up'
  const isLoginPage = pathname === '/login'
  const isForgotPasswordPage = pathname === '/forgot-password'
  
  // Check if we're on pages that should use MainNav
  const shouldUseMainNav = ['about', 'legal', 'support', 'login', 'sign-up'].includes(pathUsername)
  
  // Check if we're on a user page (single segment path that's not a special page)
  const isUserPage = !['about', 'legal', 'login', 'sign-up', 'pricing', 'roadmap', 'changelog', 'documentation', 'tutorials', 'api', 'status', 'support'].includes(pathUsername)

  const isOwnProfile = user?.user_metadata?.username === pathUsername
  const isLoggedInOnOtherProfile = user && !isOwnProfile && currentProfile
  const shouldShowMinimalHeader = !user || (!isOwnProfile && currentProfile) // Used for py-1 and logo size/container
  const isAdminMode = user?.user_metadata?.is_admin && !isOwnProfile && currentProfile

  useEffect(() => {
    if (isHomePage || isSignUpPage || isLoginPage || isForgotPasswordPage || !isUserPage) return;

    const fetchUserData = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      setUser(session?.user || null)

      if (pathUsername) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('username', pathUsername)
          .single()
        setCurrentProfile(profile)
      }
    }

    fetchUserData()

    const { data: authListener } = supabase.auth.onAuthStateChange((_, session) => {
      setUser(session?.user || null)
    })

    return () => {
      // Make sure to check if authListener and its unsubscribe method exist
      if (authListener && typeof authListener.unsubscribe === 'function') {
        authListener.unsubscribe()
      }
    }
  }, [pathUsername, isHomePage, isSignUpPage, isLoginPage, isForgotPasswordPage, isUserPage])

  useEffect(() => {
    const handleProfileScroll = (event) => {
      const currentScrollY = event.detail.scrollTop
      
      if (scrollTimer) {
        clearTimeout(scrollTimer)
      }

      const newTimer = setTimeout(() => {
        let newHeaderState = headerState

        if (currentScrollY < 10) {
          newHeaderState = 'full'
        } else if (currentScrollY > 250) {
          if (isLoggedInOnOtherProfile && (currentScrollY > lastScrollY + 10)) {
            newHeaderState = 'full'
          } else {
            newHeaderState = 'icon'
          }
        } else { 
          if (currentScrollY < lastScrollY - 300) { 
            newHeaderState = 'full'
          } else if (currentScrollY > lastScrollY + 10) { 
            if (isLoggedInOnOtherProfile) {
              newHeaderState = 'full' 
            } else {
              newHeaderState = 'hidden'
            }
          } else {
            if (headerState === 'hidden') {
              newHeaderState = 'hidden'
            } else if (headerState === 'icon' && currentScrollY <= 250) { 
              newHeaderState = 'full'; 
            }
            else {
              newHeaderState = 'full' 
            }
          }
        }

        if (headerState !== newHeaderState) {
          setHeaderState(newHeaderState)
          emitHeaderVisibility({ state: newHeaderState, scrollTop: currentScrollY })
        }
        setLastScrollY(currentScrollY)
      }, 100)

      setScrollTimer(newTimer)
    }

    document.addEventListener('profileScroll', handleProfileScroll)
    return () => {
      document.removeEventListener('profileScroll', handleProfileScroll)
      if (scrollTimer) clearTimeout(scrollTimer)
    }
  }, [lastScrollY, scrollTimer, isLoggedInOnOtherProfile, headerState])

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut()
      if (error) {
        console.error('Error signing out:', error.message)
        return
      }
      setUser(null)
      setCurrentProfile(null)
      router.push('/login')
    } catch (error) {
      console.error('Error during logout:', error)
    }
  }

  const handleLogoClick = async () => {
    if (user) {
      if (pathname === '/') {
        const { data, error } = await supabase
          .from('profiles')
          .select('username')
          .eq('id', user.id)
          .single()

        if (!error && data?.username) {
          router.push(`/${data.username}`)
        } else {
          router.push('/')
        }
      } else {
        router.push(pathname)
      }
    } else {
      router.push('/')
    }
  }

  // Updated icon click handler
  const handleIconClick = () => { // No async needed as router.push is not awaited here
    if (user && user.user_metadata?.username) {
      router.push(`/${user.user_metadata.username}/dashboard/`);
    } else {
      router.push('/');
    }
  };

  if (shouldUseMainNav) {
    return <MainNav />
  }

  if (!isUserPage || isHomePage || isSignUpPage || isLoginPage || isForgotPasswordPage) {
    return null; // No header or icon on these pages
  }

  return (
    <>
      {/* Floating AF Icon - enhanced style and transition */}
      <div
        onClick={handleIconClick}
        className={`
          fixed top-4 left-4 w-12 h-12
          bg-gradient-to-r from-indigo-600 to-purple-800
          text-white flex items-center justify-center rounded-full
          shadow-xl hover:shadow-2xl
          cursor-pointer z-[1001]
          transition-all duration-300 ease-in-out
          ${spaceGrotesk.className} text-xl
          ${headerState === 'icon' ? 'opacity-100 translate-x-0 scale-100' : 'opacity-0 -translate-x-[150%] scale-75 pointer-events-none'}
        `}
        title="Autofocus Home/Dashboard"
      >
        AF
      </div>

      {/* Main Header: Rendered conditionally if not 'hidden', visibility controlled by classes */}
      {headerState !== 'hidden' && (
        <header className={`
          h-8 
          bg-gradient-to-r from-indigo-600 to-purple-800
          fixed top-0 left-0 right-0 w-full z-[1000]
          backdrop-blur-sm bg-opacity-95
          transform transition-all duration-300 ease-in-out
          ${headerState === 'full' ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0 pointer-events-none'}
        `}>
          <div className={`
            max-w-7xl mx-auto px-2
            h-full flex items-center justify-between
            ${shouldShowMinimalHeader ? 'py-1' : ''} 
          `}>
            <div className={`
              logo-container flex items-center
              ${shouldShowMinimalHeader ? 'flex-grow justify-center' : ''}
              -mt-0.5
              opacity-100 
            `}>
              {isAdminMode ? (
                <div className="text-white font-bold tracking-wider">
                  <span className="hidden sm:inline">ADMIN MODE</span>
                  <span className="sm:hidden">ADMIN</span>
                </div>
              ) : (
                <div 
                  onClick={handleLogoClick} 
                  className="cursor-pointer"
                >
                  <div className="hidden sm:block">
                    <Logo 
                      style="inverted" 
                      path={pathUsername} 
                      size={shouldShowMinimalHeader ? "small" : "large"}
                    />
                  </div>
                  <div className="sm:hidden">
                    <Logo 
                      style="abbreviatedUrl" 
                      path={pathUsername} 
                      size={shouldShowMinimalHeader ? "small" : "medium"}
                    />
                  </div>
                </div>
              )}
            </div>

            {isLoggedInOnOtherProfile ? (
              <div className="flex items-center space-x-4">
                <HeaderDropdown user={user} handleLogout={handleLogout} minimal={true} />
              </div>
            ) : user && !shouldShowMinimalHeader ? (
              <>
                <HeaderDropdown user={user} handleLogout={handleLogout} />
              </>
            ) : (
              <div className="flex-grow" />
            )}
          </div>
        </header>
      )}
    </>
  )
}